import React, {useState, useEffect} from 'react';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPenToSquare} from '@fortawesome/free-solid-svg-icons';
import se from 'languages/se';
import strings from 'utils/strings';
import getLanguageConfig from 'api/getLanguageConfig';
import {saveLanguageKey, deleteLanguageKey} from 'api/saveLanguageKey';
import {applyOverrides, readLangCache, writeLangCache} from 'utils/strings';

// se.js is now nested (namespace → keys). Flatten it so the Config UI and
// the DB both work with plain { key: value } pairs.
function flattenLocale(obj) {
	const flat = {};
	for (const nsKeys of Object.values(obj)) {
		if (nsKeys && typeof nsKeys === 'object') Object.assign(flat, nsKeys);
	}
	return flat;
}

// Build key → namespace map so the UI can show which feature each key belongs to.
function buildKeyNsMap(obj) {
	const map = {};
	for (const [ns, nsKeys] of Object.entries(obj)) {
		if (nsKeys && typeof nsKeys === 'object') {
			for (const key of Object.keys(nsKeys)) map[key] = ns;
		}
	}
	return map;
}

const staticDefaults = {se: flattenLocale(se)};
const KEY_NS_MAP = {se: buildKeyNsMap(se)};
const APP = 'backoffice';

const readCache  = () => readLangCache(APP);
const writeCache = (data) => writeLangCache(APP, data);

function formatDate(iso) {
	if (!iso) return null;
	const d = new Date(iso);
	return d.toLocaleDateString('en-GB', {day: '2-digit', month: 'short', year: 'numeric'});
}

// Returns true if the placeholder (e.g. "{0}") appears in `draft` as a bare token —
// not immediately preceded by [ ( $ * _ # or followed by ] ) $ * _ #
// which would indicate the user wrapped it in decorative symbols.
function hasBarePlaceholder(draft, p) {
	const BAD_BEFORE = new Set(['[', '(', '$', '*', '_', '#', '{']);
	const BAD_AFTER  = new Set([']', ')', '$', '*', '_', '#', '}']);
	let idx = 0;
	while ((idx = draft.indexOf(p, idx)) !== -1) {
		const before = idx > 0 ? draft[idx - 1] : '';
		const after  = idx + p.length < draft.length ? draft[idx + p.length] : '';
		if (!BAD_BEFORE.has(before) && !BAD_AFTER.has(after)) return true;
		idx++;
	}
	return false;
}

function flatValues(overrides) {
	const flat = {};
	Object.keys(overrides).forEach((loc) => {
		flat[loc] = {};
		Object.keys(overrides[loc]).forEach((k) => {
			if (k === '_meta') return;
			flat[loc][k] = overrides[loc][k];
		});
	});
	return flat;
}

const Config = () => {
	const [locale, setLocale] = useState('se');
	const [overrides, setOverrides] = useState({});
	const [drafts, setDrafts] = useState({});
	const [search, setSearch] = useState('');
	const [editingKey, setEditingKey] = useState(null);
	const [saving, setSaving] = useState(null);
	const [saved, setSaved] = useState(null);
	const [error, setError] = useState(null);
	const [showOverridden, setShowOverridden] = useState(false);

	const fetchFresh = () => {
		localStorage.removeItem(`lang_config_${APP}`);
		getLanguageConfig(APP).then((data) => {
			setOverrides(data);
			setDrafts(flatValues(data));
			writeCache(data);
		});
	};

	useEffect(() => {
		const cached = readCache();
		if (cached) {
			setOverrides(cached);
			setDrafts(flatValues(cached));
			return;
		}
		fetchFresh();
	}, []);

	const baseStrings = staticDefaults[locale] || {};
	const localeOverrides = overrides[locale] || {};

	const overriddenKeys = Object.keys(localeOverrides).filter(
		(k) => k !== '_meta' && k in baseStrings && localeOverrides[k] !== baseStrings[k]
	);
	const hasOverrides = overriddenKeys.length > 0;

	const searchLower = search.toLowerCase();
	const visibleKeys = Object.keys(baseStrings).filter((key) => {
		if (showOverridden && !(key in localeOverrides && localeOverrides[key] !== baseStrings[key])) return false;
		if (search) {
			if (key.toLowerCase().includes(searchLower)) return true;
			const ns = (KEY_NS_MAP[locale]?.[key] || '').toLowerCase();
			if (ns.includes(searchLower)) return true;
			const defaultVal = String(baseStrings[key] ?? '').toLowerCase();
			const overrideVal = String(localeOverrides[key] ?? '').toLowerCase();
			return defaultVal.includes(searchLower) || overrideVal.includes(searchLower);
		}
		return true;
	});

	const handleSave = async (key) => {
		const value = drafts[locale]?.[key] ?? baseStrings[key];
		setSaving(key);
		setError(null);
		try {
			const result = await saveLanguageKey(locale, key, value, APP);
			const updated = {
				...overrides,
				[locale]: {
					...localeOverrides,
					[key]: value,
					_meta: {
						updatedAt: result?.updatedAt ?? new Date().toISOString(),
						updatedBy: result?.updatedBy ?? null,
					},
				},
			};
			setOverrides(updated);
			writeCache(updated);
			applyOverrides(flatValues(updated));
			setSaved(key);
			setTimeout(() => {
				setSaved(null);
				setEditingKey(null);
			}, 1200);
		} catch {
			setError(`Failed to save ${key}`);
		} finally {
			setSaving(null);
		}
	};

	const handleReset = async (key) => {
		setError(null);
		try {
			await deleteLanguageKey(locale, key, APP);
			const updatedLocale = {...localeOverrides};
			delete updatedLocale[key];
			const updated = {...overrides, [locale]: updatedLocale};
			setOverrides(updated);
			writeCache(updated);
			setDrafts((prev) => {
				const next = {...prev, [locale]: {...(prev[locale] || {})}};
				delete next[locale][key];
				return next;
			});
			applyOverrides(flatValues(updated));
			setEditingKey(null);
		} catch {
			setError(`Failed to reset ${key}`);
		}
	};

	const handleDraftChange = (key, value) => {
		setDrafts((prev) => ({
			...prev,
			[locale]: {...(prev[locale] || {}), [key]: value},
		}));
	};

	return (
		<div className="lit-config-panel">
			<div className="lit-config-controls">
				<div className="lit-config-locale-switcher">
					{Object.keys(staticDefaults).map((l) => (
						<button
							key={l}
							className={`lit-btn${locale === l ? ' lit-btn--active' : ''}`}
							onClick={() => {
								setLocale(l);
								setEditingKey(null);
							}}
						>
							{l.toUpperCase()}
						</button>
					))}
				</div>
				<input
					className="lit-input__field lit-config-search"
					placeholder={strings.controlPanel.configSearchPlaceholder}
					value={search}
					onChange={(e) => setSearch(e.target.value)}
				/>
				<div className="lit-dropdown_filter">
					<select
						className="lit-input__field"
						value={showOverridden ? 'overridden' : 'all'}
						onChange={(e) => setShowOverridden(e.target.value === 'overridden')}
						disabled={!hasOverrides && !showOverridden}
					>
						<option value="all">{strings.controlPanel.configAllaOption}</option>
						<option value="overridden">{`${strings.controlPanel.configOverriddenOption}${hasOverrides ? ` (${overriddenKeys.length})` : ''}`}</option>
					</select>
				</div>
				<p className="lit-config-hint">
					{search
						? strings.formatString(strings.controlPanel.configKeysFound, visibleKeys.length)
						: strings.formatString(strings.controlPanel.configKeysSummary, visibleKeys.length, overriddenKeys.length)}
					{' · '}
					<span className="lit-config-cache-note">{strings.controlPanel.configCacheNote}</span>
					{' · '}
					<button className="lit-config-jump-btn" onClick={fetchFresh}>Refresh</button>
				</p>
				{localeOverrides._meta?.updatedAt && (
					<p className="lit-config-locale-meta">
						{strings.controlPanel.configLastEdited}<span className="lit-config-meta-date">{formatDate(localeOverrides._meta.updatedAt)}</span>
						{localeOverrides._meta.updatedBy && (
							<span className="lit-config-meta-by">{strings.controlPanel.configEditedBy}{localeOverrides._meta.updatedBy}</span>
						)}
					</p>
				)}
			</div>

			{error && <p className="lit-config-error">{error}</p>}

			{visibleKeys.length === 0 && showOverridden && !search && (
				<div className="lit-config-empty">
				<p>{strings.formatString(strings.controlPanel.configNoOverriddenKeys, locale.toUpperCase())}</p>
				</div>
			)}
			{visibleKeys.length === 0 && search && (
				<div className="lit-config-empty">
					<p>{strings.controlPanel.configNoKeysMatch}</p>
				</div>
			)}

			<div className="lit-config-table-wrap">
				<table className="lit-config-table">
					<thead>
						<tr>
							<th>{strings.controlPanel.configKeyHeader}</th>
							<th>{strings.controlPanel.configValueHeader}</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						{visibleKeys.map((key) => {
							const defaultVal = baseStrings[key];
							const overrideEntry = localeOverrides[key];
							const isOverridden = key in localeOverrides && key !== '_meta' && localeOverrides[key] !== baseStrings[key];
								const overriddenValue = typeof overrideEntry === 'string' ? overrideEntry : null;
							const displayValue = isOverridden ? overriddenValue : defaultVal;
							const draftVal = drafts[locale]?.[key] ?? displayValue;
							const isDirty = draftVal !== displayValue;
							const isEditing = editingKey === key;

							return (
								<React.Fragment key={key}>
									<tr className={`lit-config-tr${isOverridden ? ' lit-config-tr--overridden' : ''}`}>
										<td className="lit-config-td-key" data-label="Key">
											<span className="lit-config-ns-badge">{KEY_NS_MAP[locale]?.[key]}</span>
											<span className="lit-config-key">{key}</span>
											{isOverridden && (
											<span className="lit-config-badge">{strings.controlPanel.configOverriddenBadge}</span>
											)}
										</td>
										<td className="lit-config-td-value" data-label="Value">{displayValue}</td>

										<td className="lit-config-td-actions" data-label="">
											<button
												className="lit-mytests-header-mytest-icon-btn-primary"
												title={strings.controlPanel.configEditTitle}
												onClick={() => setEditingKey(isEditing ? null : key)}
											>
												<FontAwesomeIcon size="lg" className="lit-mytests-header-mytest-icon-btn-icon" icon={faPenToSquare} />
											</button>
										</td>
									</tr>
									{isEditing && (
										<tr className="lit-config-edit-row">
											<td colSpan={4}>
												<div className="lit-config-edit-box">
													<p className="lit-config-default">
													<strong>{strings.controlPanel.configDefaultLabel}</strong>{' '}
														{String(defaultVal).split(/(\{[0-9]+\})/g).map((part, i) =>
															/^\{[0-9]+\}$/.test(part)
																? <code key={i} className="lit-config-placeholder-tag lit-config-placeholder-inline">{part}</code>
																: part
														)}
													</p>
													{(() => {
														const placeholders = [...new Set((String(defaultVal).match(/\{[0-9]+\}/g) || []))].sort();
														const missingInDraft = placeholders.filter((p) => !hasBarePlaceholder(String(draftVal), p));
														const getSnippet = (p) => {
															const str = String(defaultVal);
															const idx = str.indexOf(p);
															if (idx === -1) return null;
															const start = Math.max(0, idx - 18);
															const end = Math.min(str.length, idx + p.length + 18);
															return {
																prefix: `${start > 0 ? '…' : ''}${str.slice(start, idx)}`,
																suffix: `${str.slice(idx + p.length, end)}${end < str.length ? '…' : ''}`,
															};
														};
														return placeholders.length > 0 ? (
															<div className="lit-config-placeholders">
																<span className="lit-config-placeholder-label">{strings.controlPanel.configRequiredPlaceholders}</span>
																{placeholders.map((p) => (
																	<code key={p} className={`lit-config-placeholder-tag${missingInDraft.includes(p) ? ' lit-config-placeholder-missing' : ''}`}>{p}</code>
																))}
																{missingInDraft.length > 0 && (
																	<div className="lit-config-placeholder-missing-hints">
																			<span className="lit-config-placeholder-warning">{strings.controlPanel.configCannotSaveWarning}</span>
																		{missingInDraft.map((p) => {
																			const snippet = getSnippet(p);
																			return (
																				<div key={p} className="lit-config-placeholder-hint">
																					<code className="lit-config-placeholder-tag lit-config-placeholder-missing">{p}</code>
																					{snippet && (
																						<span className="lit-config-placeholder-hint-context">
																							{strings.controlPanel.configBelongsHere}
																							<em>{snippet.prefix}</em>
																							<code className="lit-config-placeholder-tag lit-config-placeholder-missing">{p}</code>
																							<em>{snippet.suffix}</em>
																						</span>
																					)}
																				</div>
																			);
																		})}
																	</div>
																)}
															</div>
														) : null;
													})()}
													<textarea
														className="lit-input__field lit-config-textarea"
														value={draftVal}
														rows={Math.min(6, Math.max(2, Math.ceil(String(defaultVal).length / 80)))}
														onChange={(e) => handleDraftChange(key, e.target.value)}
													/>
													<div className="lit-config-edit-actions">
														{(() => {
															const placeholders = [...new Set((String(defaultVal).match(/\{[0-9]+\}/g) || []))].sort();
													const missingInDraft = placeholders.filter((p) => !hasBarePlaceholder(String(draftVal), p));
															const hasMissingPlaceholders = missingInDraft.length > 0;
															return (
																<button
																	className="lit-btn lit-btn--small bg-positive"
																	onClick={() => handleSave(key)}
																	disabled={saving === key || !isDirty || hasMissingPlaceholders}
																title={hasMissingPlaceholders ? strings.formatString(strings.controlPanel.configAddMissingPlaceholders, missingInDraft.join(', ')) : undefined}
																>
																	{saved === key ? strings.controlPanel.configSavedConfirm : saving === key ? strings.controlPanel.configSavingLabel : strings.controlPanel.configSaveLabel}
																</button>
															);
														})()}
														{isOverridden && (
															<button
																className="lit-btn lit-btn--small bg-danger"
																onClick={() => handleReset(key)}
															>
																{strings.controlPanel.configResetToDefault}
															</button>
														)}
														<button
															className="lit-btn lit-btn--small bg-secondary"
															onClick={() => setEditingKey(null)}
														>
															{strings.controlPanel.configCancel}
														</button>
													</div>
												</div>
											</td>
										</tr>
									)}
								</React.Fragment>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
};

export default Config;
